import React, { useEffect, useRef, useState } from "react";
import { FiCheck, FiChevronDown } from "react-icons/fi";

export default function MobileCategorySelect({ categories, value, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const optionRefs = useRef([]);
  const focusOnOpen = useRef(null);
  const selected = categories.find((category) => category.id === value) || categories[0];

  useEffect(() => setIsOpen(false), [value]);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;
    const media = window.matchMedia("(max-width: 991px)");
    const closeOnDesktop = () => {
      if (!media.matches) setIsOpen(false);
    };
    media.addEventListener("change", closeOnDesktop);
    closeOnDesktop();
    return () => media.removeEventListener("change", closeOnDesktop);
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    if (focusOnOpen.current !== null) {
      optionRefs.current[focusOnOpen.current]?.focus();
      focusOnOpen.current = null;
    }
    const closeOutside = (event) => {
      if (!containerRef.current?.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("focusin", closeOutside);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("focusin", closeOutside);
    };
  }, [isOpen]);

  const closeAndFocus = () => {
    setIsOpen(false);
    triggerRef.current?.focus({ preventScroll: true });
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape" && isOpen) {
      event.preventDefault();
      event.stopPropagation();
      closeAndFocus();
      return;
    }
    if (event.target === triggerRef.current && ["ArrowDown", "ArrowUp"].includes(event.key)) {
      event.preventDefault();
      const index = categories.indexOf(selected);
      if (isOpen) optionRefs.current[index]?.focus();
      else {
        focusOnOpen.current = index;
        setIsOpen(true);
      }
      return;
    }
    const index = optionRefs.current.indexOf(event.target);
    if (!isOpen || index < 0) return;
    const nextIndex = {
      ArrowDown: (index + 1) % categories.length,
      ArrowUp: (index - 1 + categories.length) % categories.length,
      Home: 0,
      End: categories.length - 1,
    }[event.key];
    if (nextIndex !== undefined) {
      event.preventDefault();
      optionRefs.current[nextIndex]?.focus();
    }
  };

  return (
    <div className="catalog-category-mobile" ref={containerRef} onKeyDown={handleKeyDown}>
      <span className="catalog-category-label" id="product-category-label">Browse by category</span>
      <button type="button" id="product-category" className="catalog-category-trigger"
        ref={triggerRef} aria-labelledby="product-category-label" aria-describedby="product-category-value"
        aria-expanded={isOpen} aria-controls="product-category-options" onClick={() => setIsOpen(!isOpen)}>
        <span id="product-category-value">{selected.name} <span>({selected.count})</span></span>
        <FiChevronDown aria-hidden="true" />
      </button>
      {isOpen && (
        <ul id="product-category-options" className="catalog-category-options" aria-label="Choose a product category">
          {categories.map((category, index) => (
            <li key={category.id}>
              <button type="button" ref={(element) => { optionRefs.current[index] = element; }}
                aria-label={`${category.name} (${category.count})`} aria-pressed={category.id === selected.id}
                className={category.id === selected.id ? "is-selected" : ""}
                onClick={() => { onChange(category.id); closeAndFocus(); }}>
                <span className="catalog-category-option-name">{category.name}</span>
                <span className="category-count">{category.count}</span>
                <FiCheck className="catalog-category-check" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
